import React, { useState, useEffect, useRef } from 'react';
import { Plus, Search, Filter, Eye, Edit, Trash2, User, Mail, Phone, Calendar, Shield, Users, MoreVertical, X, MapPin, Building } from 'lucide-react';
import { User as UserType } from '../types';
import CommonActionBar from '../components/CommonActionBar';

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<UserType[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [departmentFilter, setDepartmentFilter] = useState<string>('all');
  const [presenceFilter, setPresenceFilter] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserType | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Mock data for demonstration
  const mockUsers: UserType[] = [
    {
      id: 1,
      name: 'Rajesh Kumar',
      email: 'rajesh.kumar@hotelcontinental.com',
      phone: '+91 98765 43210',
      role: 'admin',
      department: 'management',
      status: 'active',
      joinDate: '2023-01-15',
      lastLogin: '2025-01-09T10:30:00',
      permissions: ['all'],
      avatar: '/images/users/rajesh-kumar.webp',
      presence: 'present'
    },
    {
      id: 2,
      name: 'Priya Sharma',
      email: 'priya.sharma@hotelcontinental.com',
      phone: '+91 98765 43211',
      role: 'manager',
      department: 'restaurant',
      status: 'active',
      joinDate: '2023-03-20',
      lastLogin: '2025-01-09T09:15:00',
      permissions: ['orders', 'menu', 'staff'],
      avatar: '/images/users/priya-sharma.webp',
      presence: 'present'
    },
    {
      id: 3,
      name: 'Amit Singh',
      email: 'amit.singh@hotelcontinental.com',
      phone: '+91 98765 43212',
      role: 'kitchen',
      department: 'kitchen',
      status: 'active',
      joinDate: '2023-05-10',
      lastLogin: '2025-01-09T08:45:00',
      permissions: ['orders', 'kitchen'],
      avatar: '/images/users/amit-singh.webp',
      presence: 'present'
    },
    {
      id: 4,
      name: 'Kavya Reddy',
      email: 'kavya.reddy@hotelcontinental.com',
      phone: '+91 98765 43213',
      role: 'waiter',
      department: 'restaurant',
      status: 'active',
      joinDate: '2023-07-05',
      lastLogin: '2025-01-09T11:20:00',
      permissions: ['orders', 'tables'],
      avatar: '/images/users/kavya-reddy.webp',
      presence: 'present'
    },
    {
      id: 5,
      name: 'Vikram Patel',
      email: 'vikram.patel@hotelcontinental.com',
      phone: '+91 98765 43214',
      role: 'staff',
      department: 'front_desk',
      status: 'inactive',
      joinDate: '2023-09-12',
      lastLogin: '2025-01-05T16:30:00',
      permissions: ['bookings', 'rooms'],
      avatar: '/images/users/vikram-patel.webp',
      presence: 'absent'
    },
    {
      id: 6,
      name: 'Sunita Gupta',
      email: 'sunita.gupta@hotelcontinental.com',
      phone: '+91 98765 43215',
      role: 'staff',
      department: 'housekeeping',
      status: 'active',
      joinDate: '2023-11-08',
      lastLogin: '2025-01-09T07:00:00',
      permissions: ['rooms', 'housekeeping'],
      avatar: '/images/users/sunita-gupta.webp',
      presence: 'present',
      zones: ['North Wing', 'Garden View'],
      floors: ['1st Floor', '2nd Floor']
    },
    {
      id: 7,
      name: 'Ravi Verma',
      email: 'ravi.verma@hotelcontinental.com',
      phone: '+91 98765 43216',
      role: 'waiter',
      department: 'restaurant',
      status: 'active',
      joinDate: '2023-12-15',
      lastLogin: '2025-01-09T12:30:00',
      permissions: ['orders', 'tables'],
      avatar: '/images/users/ravi-verma.webp',
      presence: 'present'
    },
    {
      id: 8,
      name: 'Meera Joshi',
      email: 'meera.joshi@hotelcontinental.com',
      phone: '+91 98765 43217',
      role: 'manager',
      department: 'front_desk',
      status: 'active',
      joinDate: '2023-08-22',
      lastLogin: '2025-01-09T11:45:00',
      permissions: ['bookings', 'rooms', 'staff'],
      avatar: '/images/users/meera-joshi.webp',
      presence: 'present'
    },
    {
      id: 9,
      name: 'Arjun Singh',
      email: 'arjun.singh@hotelcontinental.com',
      phone: '+91 98765 43218',
      role: 'kitchen',
      department: 'kitchen',
      status: 'active',
      joinDate: '2023-06-10',
      lastLogin: '2025-01-09T09:20:00',
      permissions: ['orders', 'kitchen'],
      avatar: '/images/users/arjun-singh.webp',
      presence: 'present'
    },
    {
      id: 10,
      name: 'Deepika Patel',
      email: 'deepika.patel@hotelcontinental.com',
      phone: '+91 98765 43219',
      role: 'staff',
      department: 'housekeeping',
      status: 'active',
      joinDate: '2023-10-05',
      lastLogin: '2025-01-09T08:15:00',
      permissions: ['rooms', 'housekeeping'],
      avatar: '/images/users/deepika-patel.webp',
      presence: 'present',
      zones: ['South Wing', 'Pool Area'],
      floors: ['3rd Floor', '4th Floor']
    },
    {
      id: 11,
      name: 'Suresh Kumar',
      email: 'suresh.kumar@hotelcontinental.com',
      phone: '+91 98765 43220',
      role: 'waiter',
      department: 'restaurant',
      status: 'active',
      joinDate: '2023-09-18',
      lastLogin: '2025-01-09T13:10:00',
      permissions: ['orders', 'tables'],
      avatar: '/images/users/suresh-kumar.webp',
      presence: 'present'
    },
    {
      id: 12,
      name: 'Anita Sharma',
      email: 'anita.sharma@hotelcontinental.com',
      phone: '+91 98765 43221',
      role: 'staff',
      department: 'front_desk',
      status: 'inactive',
      joinDate: '2023-07-30',
      lastLogin: '2025-01-08T17:45:00',
      permissions: ['bookings', 'rooms'],
      avatar: '/images/users/anita-sharma.webp',
      presence: 'absent'
    },
    {
      id: 13,
      name: 'Vikash Gupta',
      email: 'vikash.gupta@hotelcontinental.com',
      phone: '+91 98765 43222',
      role: 'kitchen',
      department: 'kitchen',
      status: 'active',
      joinDate: '2023-04-12',
      lastLogin: '2025-01-09T10:30:00',
      permissions: ['orders', 'kitchen'],
      avatar: '/images/users/vikash-gupta.webp',
      presence: 'present'
    },
    {
      id: 14,
      name: 'Pooja Reddy',
      email: 'pooja.reddy@hotelcontinental.com',
      phone: '+91 98765 43223',
      role: 'manager',
      department: 'restaurant',
      status: 'active',
      joinDate: '2023-02-28',
      lastLogin: '2025-01-09T14:20:00',
      permissions: ['orders', 'menu', 'staff'],
      avatar: '/images/users/pooja-reddy.webp',
      presence: 'present'
    },
    {
      id: 15,
      name: 'Manoj Tiwari',
      email: 'manoj.tiwari@hotelcontinental.com',
      phone: '+91 98765 43224',
      role: 'staff',
      department: 'housekeeping',
      status: 'active',
      joinDate: '2023-12-01',
      lastLogin: '2025-01-09T06:45:00',
      permissions: ['rooms', 'housekeeping'],
      avatar: '/images/users/manoj-tiwari.webp',
      presence: 'present',
      zones: ['East Wing', 'Executive Floor'],
      floors: ['5th Floor', '6th Floor']
    }
  ];

  const roleOptions = [
    { value: 'all', label: 'All Roles' },
    { value: 'admin', label: 'Admin' },
    { value: 'manager', label: 'Manager' },
    { value: 'staff', label: 'Staff' },
    { value: 'kitchen', label: 'Kitchen' },
    { value: 'waiter', label: 'Waiter' },
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'suspended', label: 'Suspended' },
  ];

  const departmentOptions = [
    { value: 'all', label: 'All Departments' },
    { value: 'management', label: 'Management' },
    { value: 'restaurant', label: 'Restaurant' },
    { value: 'kitchen', label: 'Kitchen' },
    { value: 'front_desk', label: 'Front Desk' },
    { value: 'housekeeping', label: 'Housekeeping' },
  ];

  const presenceOptions = [
    { value: 'all', label: 'All Staff' },
    { value: 'present', label: 'Present' },
    { value: 'absent', label: 'Absent' },
  ];

  const roleColors = {
    admin: 'bg-red-100 text-red-800',
    manager: 'bg-blue-100 text-blue-800',
    staff: 'bg-green-100 text-green-800',
    kitchen: 'bg-orange-100 text-orange-800',
    waiter: 'bg-purple-100 text-purple-800',
  };

  const statusColors = {
    active: 'bg-green-100 text-green-800',
    inactive: 'bg-gray-100 text-gray-800',
    suspended: 'bg-red-100 text-red-800',
  };

  const presenceColors = {
    present: 'bg-green-100 text-green-800 border-green-200',
    absent: 'bg-red-100 text-red-800 border-red-200',
  };

  const departmentColors = {
    management: 'bg-indigo-100 text-indigo-800',
    restaurant: 'bg-green-100 text-green-800',
    kitchen: 'bg-orange-100 text-orange-800',
    front_desk: 'bg-blue-100 text-blue-800',
    housekeeping: 'bg-pink-100 text-pink-800',
  };

  useEffect(() => {
    // Simulate loading
    setTimeout(() => {
      setUsers(mockUsers);
      setIsLoading(false);
    }, 1000);
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, roleFilter, statusFilter, departmentFilter, presenceFilter]);

  const filterUsers = () => {
    let filtered = users;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.phone?.includes(searchTerm)
      );
    }

    // Filter by role
    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => user.role === roleFilter);
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(user => user.status === statusFilter);
    }

    // Filter by department
    if (departmentFilter !== 'all') {
      filtered = filtered.filter(user => user.department === departmentFilter);
    }

    // Filter by presence
    if (presenceFilter !== 'all') {
      filtered = filtered.filter(user => user.presence === presenceFilter);
    }

    setFilteredUsers(filtered);
  };

  const handleViewUser = (user: UserType) => {
    setSelectedUser(user);
    setShowDetailsModal(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateTime: string) => {
    return new Date(dateTime).toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="glass-card p-8 rounded-2xl shadow-card text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading users...</p>
        </div>
      </div>
    );
  }

  const handleRefresh = () => {
    // Reload users
    setIsLoading(true);
    setTimeout(() => {
      setUsers(mockUsers);
      setIsLoading(false);
    }, 500);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleRoleFilterChange = (filter: string) => {
    setRoleFilter(filter);
  };

  const handleDepartmentFilterChange = (filter: string) => {
    setDepartmentFilter(filter);
  };

  const handlePresenceFilterChange = (filter: string) => {
    setPresenceFilter(filter);
  };


  return (
    <div className="space-y-6">
      {/* Common Action Bar */}
      <CommonActionBar
        showDateTime={false}
        centerContent={
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
        }
        rightActions={
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add User
          </button>
        }
        filterButtons={
          <div className="flex justify-between items-center w-full">
            {/* Presence Filters - Left */}
            <div className="flex flex-wrap gap-2">
              {presenceOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handlePresenceFilterChange(option.value)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    presenceFilter === option.value
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            
            {/* Role Filters - Center */}
            <div className="flex flex-wrap gap-2">
              {roleOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleRoleFilterChange(option.value)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    roleFilter === option.value
                      ? 'bg-primary-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            
            {/* Department Filters - Right */}
            <div className="flex flex-wrap gap-2">
              {departmentOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleDepartmentFilterChange(option.value)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    departmentFilter === option.value
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        }
        onRefresh={handleRefresh}
      />


      {/* Error State */}
      {error && (
        <div className="glass-card p-6 rounded-2xl shadow-card border-l-4 border-red-500">
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 text-red-500">⚠️</div>
            <div>
              <h3 className="font-semibold text-red-800">Error</h3>
              <p className="text-red-600">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Users List - Card Layout */}
      <div className="glass-card rounded-2xl shadow-card p-6">
        {filteredUsers.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No users found</h3>
            <p className="text-gray-500">
              {searchTerm || roleFilter !== 'all' || statusFilter !== 'all'
                ? 'Try adjusting your search or filter criteria'
                : 'Add your first user to get started'
              }
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
            {filteredUsers.map((user) => (
              <div 
                key={user.id} 
                onClick={() => handleViewUser(user)}
                className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl hover:border-primary-300 transition-all duration-300 cursor-pointer group flex flex-col h-full"
                style={{ minHeight: '400px' }}
              >
                {/* ID Card Header */}
                <div className="text-center mb-6">
                  <div className="relative inline-block">
                    {/* Photo Placeholder */}
                    <div className="w-24 h-24 bg-gradient-to-br from-primary-100 to-primary-200 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-white shadow-lg">
                      {user.avatar ? (
                        <img 
                          src={user.avatar} 
                          alt={user.name}
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        <User className="w-12 h-12 text-primary-600" />
                      )}
                    </div>
                    {/* Presence Status Badge */}
                    <div className={`absolute -bottom-2 -right-2 w-8 h-8 rounded-full border-2 border-white flex items-center justify-center ${presenceColors[user.presence || 'absent']}`}>
                      {user.presence === 'present' ? (
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                      ) : (
                        <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                      )}
                    </div>
                  </div>
                  
                  <h3 className="text-lg font-bold text-gray-900 mb-1">{user.name}</h3>
                  <p className="text-sm text-gray-500">ID: #{user.id}</p>
                </div>

                {/* Department */}
                <div className="text-center mb-4">
                  <div className="text-xs text-gray-500 mb-1">Department</div>
                  <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${departmentColors[user.department]}`}>
                    {user.department.replace('_', ' ').charAt(0).toUpperCase() + user.department.replace('_', ' ').slice(1)}
                  </span>
                  
                  {/* Zones and Floors for Housekeeping Staff */}
                  {user.department === 'housekeeping' && (
                    <div className="mt-3 space-y-2">
                      {/* Zones */}
                      {user.zones && user.zones.length > 0 && (
                        <div className="text-center">
                          <div className="text-xs text-gray-500 mb-1 flex items-center justify-center gap-1">
                            <MapPin className="w-3 h-3" />
                            Zones
                          </div>
                          <div className="flex flex-wrap justify-center gap-1">
                            {user.zones.map((zone, index) => (
                              <span key={index} className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full">
                                {zone}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Floors */}
                      {user.floors && user.floors.length > 0 && (
                        <div className="text-center">
                          <div className="text-xs text-gray-500 mb-1 flex items-center justify-center gap-1">
                            <Building className="w-3 h-3" />
                            Floors
                          </div>
                          <div className="flex flex-wrap justify-center gap-1">
                            {user.floors.map((floor, index) => (
                              <span key={index} className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full">
                                {floor}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* N/A for non-housekeeping staff */}
                  {user.department !== 'housekeeping' && (
                    <div className="mt-3 space-y-2">
                      <div className="text-center">
                        <div className="text-xs text-gray-500 mb-1 flex items-center justify-center gap-1">
                          <MapPin className="w-3 h-3" />
                          Zones
                        </div>
                        <span className="bg-gray-100 text-gray-500 text-xs px-2 py-1 rounded-full">N/A</span>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-gray-500 mb-1 flex items-center justify-center gap-1">
                          <Building className="w-3 h-3" />
                          Floors
                        </div>
                        <span className="bg-gray-100 text-gray-500 text-xs px-2 py-1 rounded-full">N/A</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Role */}
                <div className="text-center mb-4">
                  <div className="text-xs text-gray-500 mb-1">Role</div>
                  <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${roleColors[user.role]}`}>
                    {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                  </span>
                </div>

                {/* Contact Info */}
                <div className="space-y-2 mb-4 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <Mail className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-700 truncate">{user.email}</span>
                  </div>
                  {user.phone && (
                    <div className="flex items-center justify-center gap-2">
                      <Phone className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-700">{user.phone}</span>
                    </div>
                  )}
                </div>

                {/* Presence Status */}
                <div className="text-center mb-4">
                  <span className={`inline-flex px-4 py-2 text-sm font-bold rounded-full border-2 ${presenceColors[user.presence || 'absent']}`}>
                    {user.presence === 'present' ? '🟢 Present' : '🔴 Absent'}
                  </span>
                </div>


              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-primary-500 text-white p-6 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold">Add New User</h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-white hover:text-primary-100 p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <p className="text-gray-600 mb-4">User creation form will be implemented here.</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors"
                >
                  Create User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDetailsModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="bg-primary-500 text-white p-6 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary-100 to-primary-200 rounded-full flex items-center justify-center border-4 border-white shadow-lg">
                      {selectedUser.avatar ? (
                        <img 
                          src={selectedUser.avatar} 
                          alt={selectedUser.name}
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        <User className="w-8 h-8 text-primary-600" />
                      )}
                    </div>
                    <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full border-2 border-white flex items-center justify-center ${presenceColors[selectedUser.presence || 'absent']}`}>
                      {selectedUser.presence === 'present' ? (
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      ) : (
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                      )}
                    </div>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{selectedUser.name}</h2>
                    <p className="text-gray-500">ID: #{selectedUser.id}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${departmentColors[selectedUser.department]}`}>
                        {selectedUser.department.replace('_', ' ').charAt(0).toUpperCase() + selectedUser.department.replace('_', ' ').slice(1)}
                      </span>
                      <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${roleColors[selectedUser.role]}`}>
                        {selectedUser.role.charAt(0).toUpperCase() + selectedUser.role.slice(1)}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedUser(null);
                  }}
                  className="text-white hover:text-primary-100 p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Personal Information */}
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <User className="w-5 h-5 text-primary-600" />
                      Personal Information
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Full Name</label>
                        <p className="text-gray-900 font-medium">{selectedUser.name}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Email Address</label>
                        <p className="text-gray-900">{selectedUser.email}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Phone Number</label>
                        <p className="text-gray-900">{selectedUser.phone || 'Not provided'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Employee ID</label>
                        <p className="text-gray-900 font-mono">#{selectedUser.id}</p>
                      </div>
                    </div>
                  </div>

                  {/* Work Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Shield className="w-5 h-5 text-primary-600" />
                      Work Information
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Department</label>
                        <div className="mt-1">
                          <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${departmentColors[selectedUser.department]}`}>
                            {selectedUser.department.replace('_', ' ').charAt(0).toUpperCase() + selectedUser.department.replace('_', ' ').slice(1)}
                          </span>
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Role</label>
                        <div className="mt-1">
                          <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${roleColors[selectedUser.role]}`}>
                            {selectedUser.role.charAt(0).toUpperCase() + selectedUser.role.slice(1)}
                          </span>
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Status</label>
                        <div className="mt-1">
                          <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${statusColors[selectedUser.status]}`}>
                            {selectedUser.status.charAt(0).toUpperCase() + selectedUser.status.slice(1)}
                          </span>
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Current Presence</label>
                        <div className="mt-1">
                          <span className={`inline-flex px-4 py-2 text-sm font-bold rounded-full border-2 ${presenceColors[selectedUser.presence || 'absent']}`}>
                            {selectedUser.presence === 'present' ? '🟢 Present' : '🔴 Absent'}
                          </span>
                        </div>
                      </div>
                      
                      {/* Zones and Floors for Housekeeping Staff */}
                      {selectedUser.department === 'housekeeping' && (
                        <>
                          <div>
                            <label className="text-sm font-medium text-gray-500">Assigned Zones</label>
                            <div className="mt-1">
                              {selectedUser.zones && selectedUser.zones.length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                  {selectedUser.zones.map((zone, index) => (
                                    <span key={index} className="bg-blue-100 text-blue-700 text-sm px-3 py-1 rounded-full">
                                      {zone}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-gray-500 text-sm">No zones assigned</span>
                              )}
                            </div>
                          </div>
                          
                          <div>
                            <label className="text-sm font-medium text-gray-500">Assigned Floors</label>
                            <div className="mt-1">
                              {selectedUser.floors && selectedUser.floors.length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                  {selectedUser.floors.map((floor, index) => (
                                    <span key={index} className="bg-green-100 text-green-700 text-sm px-3 py-1 rounded-full">
                                      {floor}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-gray-500 text-sm">No floors assigned</span>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                      
                      {/* N/A for non-housekeeping staff */}
                      {selectedUser.department !== 'housekeeping' && (
                        <>
                          <div>
                            <label className="text-sm font-medium text-gray-500">Assigned Zones</label>
                            <div className="mt-1">
                              <span className="bg-gray-100 text-gray-500 text-sm px-3 py-1 rounded-full">N/A</span>
                            </div>
                          </div>
                          
                          <div>
                            <label className="text-sm font-medium text-gray-500">Assigned Floors</label>
                            <div className="mt-1">
                              <span className="bg-gray-100 text-gray-500 text-sm px-3 py-1 rounded-full">N/A</span>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Timeline and Permissions */}
                <div className="space-y-6">
                  {/* Timeline */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-primary-600" />
                      Timeline
                    </h3>
                    <div className="space-y-4">
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Joined Company</p>
                          <p className="text-sm text-gray-500">{formatDate(selectedUser.joinDate)}</p>
                        </div>
                      </div>
                      {selectedUser.lastLogin && (
                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">Last Login</p>
                            <p className="text-sm text-gray-500">{formatDateTime(selectedUser.lastLogin)}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Permissions */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Shield className="w-5 h-5 text-primary-600" />
                      Permissions
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedUser.permissions.map((permission, index) => (
                        <span key={index} className="bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full font-medium">
                          {permission}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Contact Actions */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Phone className="w-5 h-5 text-primary-600" />
                      Quick Actions
                    </h3>
                    <div className="grid grid-cols-2 gap-3">
                      <button className="flex items-center justify-center gap-2 bg-blue-100 text-blue-700 px-4 py-3 rounded-lg hover:bg-blue-200 transition-colors">
                        <Mail className="w-4 h-4" />
                        Email
                      </button>
                      <button className="flex items-center justify-center gap-2 bg-green-100 text-green-700 px-4 py-3 rounded-lg hover:bg-green-200 transition-colors">
                        <Phone className="w-4 h-4" />
                        Call
                      </button>
                      <button className="flex items-center justify-center gap-2 bg-purple-100 text-purple-700 px-4 py-3 rounded-lg hover:bg-purple-200 transition-colors">
                        <Edit className="w-4 h-4" />
                        Edit
                      </button>
                      <button className="flex items-center justify-center gap-2 bg-red-100 text-red-700 px-4 py-3 rounded-lg hover:bg-red-200 transition-colors">
                        <Trash2 className="w-4 h-4" />
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
